import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { PluZone } from "@/types"

const ZONE_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  U: {
    label: "Urbaine",
    className:
      "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",
  },
  AU: {
    label: "À urbaniser",
    className:
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  },
  A: {
    label: "Agricole",
    className:
      "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
  },
  N: {
    label: "Naturelle",
    className:
      "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  },
}

const CONFIDENCE_LABELS: Record<string, string> = {
  high: "Confiance haute",
  medium: "Confiance moyenne",
  low: "Confiance basse",
}

export function PluZonesTable({ zones }: { zones: PluZone[] }) {
  if (zones.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Zones PLU ({zones.length})</CardTitle>
        <CardDescription>Zones réglementaires extraites du Plan Local d'Urbanisme</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Hauteur max</TableHead>
                <TableHead className="text-right">Étages max</TableHead>
                <TableHead className="text-right">Emprise au sol</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.map((zone) => (
                <TableRow key={zone.id}>
                  <TableCell className="font-mono font-medium">{zone.zone_code}</TableCell>
                  <TableCell>
                    <ZoneTypeBadge type={zone.zone_type} />
                  </TableCell>
                  <TableCell className="text-right">
                    <ValueWithConfidence
                      value={zone.max_hauteur != null ? `${zone.max_hauteur} m` : null}
                      confidence={zone.max_hauteur_confidence}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <ValueWithConfidence
                      value={zone.max_etages != null ? `R+${zone.max_etages}` : null}
                      confidence={zone.max_etages_confidence}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <ValueWithConfidence
                      value={zone.max_emprise_au_sol != null ? `${zone.max_emprise_au_sol}%` : null}
                      confidence={zone.max_emprise_au_sol_confidence}
                    />
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm">{zone.notes || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function ZoneTypeBadge({ type }: { type: string }) {
  const config = ZONE_TYPE_CONFIG[type]
  if (!config) return <Badge variant="outline">{type}</Badge>
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  )
}

function ValueWithConfidence({ value, confidence }: { value: string | null; confidence: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>

  const confidenceColor =
    confidence === "high"
      ? "text-green-600"
      : confidence === "medium"
        ? "text-yellow-600"
        : confidence === "low"
          ? "text-red-600"
          : ""

  return (
    <span className="inline-flex items-center gap-1.5">
      {value}
      {confidence && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help text-xs font-medium uppercase ${confidenceColor}`}>{confidence[0]}</span>
          </TooltipTrigger>
          <TooltipContent>{CONFIDENCE_LABELS[confidence] ?? confidence}</TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}
